import { useEffect, useState } from 'react';
import groupByPositions from '../../maps/groupByPositions';
import mergeTerritoryData from '../../maps/mergeTerritoryData';
import first from 'lodash.first';
import { PromiseState } from 'react-refetch';
import escapeRegExp from 'helpers/escapeRegExp';
import { PositionVariations } from 'types/shared';
import { ICountryFlagProps } from 'types/shared';
import { IGetChartPositions } from '../shared';

/**
 * This hook creates a combined list of weekly and daily chart positions grouped by position and with its
 * territory data merged with the specified country metadata
 *
 * @param dailyChartPositions a PromiseState that returns an array of daily chart positions
 * @param weeklyChartPositions a PromiseState that returns an array of weekly chart positions
 * @param countries an array of country metadata
 * @param  positionKey The type of table that we want to show
 * @returns a stateful list of chart details grouped by position
 */

export default function useChartPositions(
  dailyChartPositions: PromiseState<any>,
  weeklyChartPositions: PromiseState<any>,
  countries: ICountryFlagProps[] | undefined,
  positionKey: PositionVariations
): any[] | null {
  const [charts, setCharts] = useState<any[] | null>(null);

  /**
   * When the country meta data and daily and weekly chart data comes in and is fulfilled,
   * we want to group the data by positions and merge the territory data
   * from charts with the country meta data.
   */
  useEffect(() => {
    if (dailyChartPositions.value && weeklyChartPositions.value && countries) {
      setCharts(
        mergeCharts({
          dailyChartPositions: dailyChartPositions.value,
          weeklyChartPositions: weeklyChartPositions.value,
          positionKey,
          countries,
        })
      );
    }
  }, [
    dailyChartPositions.value,
    weeklyChartPositions.value,
    positionKey,
    countries,
    dailyChartPositions,
    weeklyChartPositions,
  ]);

  return charts;
}

/**
 * Returns a method that will first check if the given chart.displayName already
 * ends with the specified suffix. If this is NOT the case, the suffix is added
 * to the end of the displayName. If it does exist, it gets replaced by the given
 * suffix to normalize the capitalization. (I.e. weekly gets replaced by Weekly)
 * @param suffix The specified string with which the displayName propert will be suffixed
 */
export function addToName(suffix: string): (chart: any) => any {
  const regex = new RegExp(`${escapeRegExp(suffix)}$`, 'i');
  return function (chart: any): any {
    // during iteration assignments mutate the original array
    const copy = Object.assign({}, chart);
    const { chartDisplayName } = copy;
    if (!regex.test(chartDisplayName)) {
      copy.chartDisplayName = `${chartDisplayName}${suffix}`;
    } else {
      copy.chartDisplayName = chartDisplayName.replace(regex, suffix);
    }
    return copy;
  };
}

/**
 * Group the data by positions and merge the territory data from charts with the country meta data.
 */
export function mergeCharts({
  dailyChartPositions,
  weeklyChartPositions,
  positionKey,
  countries,
}: {
  dailyChartPositions: IGetChartPositions;
  weeklyChartPositions: IGetChartPositions;
  positionKey: PositionVariations;
  countries: ICountryFlagProps[];
}): any[] {
  const getCharts = (obj: any): any => first<any>(obj.entityData).charts;
  const dailyCharts = getCharts(dailyChartPositions);
  const weeklyCharts = getCharts(weeklyChartPositions);
  const addDailyToName = addToName(' Daily');
  const addWeeklyToName = addToName(' Weekly');

  const combinedCharts = [
    ...weeklyCharts.map(addWeeklyToName),
    ...dailyCharts.map(addDailyToName),
  ];
  const groupedCharts = groupByPositions(combinedCharts, positionKey);

  return mergeTerritoryData(groupedCharts, countries);
}
